import { useEffect, useMemo } from 'react'
import clsx from 'clsx'
import i18n from 'src/i18n'
import { Slider } from 'src/components/slider'
import { useSessionData } from 'src/hook/useSessionData'
import { useTrackEvents } from 'src/context/userProfileContext/hooks'
import { TryResult } from 'src/utils/datahubDefaultEvent'
import { useUserProfile } from 'src/components/globalUserProfile'
import { CoursesErrorState } from '../coursesErrorState'
import { HighlightCardSkeleton } from '../highlightCardSkeleton'
import { useGetUserData } from '../../hooks'
import { CardAmount, CardLabel, SliderWrapper, Title, Wrapper } from './highlights.styles'
import { HighlightsProps } from './highlights.types'
import { sliderSettings } from './highlights.constants'
import { getHighlights } from './highlights.utils'

const Highlights = ({ className }: HighlightsProps) => {
  const { user } = useSessionData()
  const { visible, userUcode = user?.ucode, loggedUserUcode } = useUserProfile()
  const { sendLoadHighlightsEvent } = useTrackEvents({
    loggedUserUcode
  })

  const { isLoading, isError, isSuccess, data, error } = useGetUserData({
    ucode: userUcode,
    enabled: visible
  })

  const pagesCompleted = data?.stats?.pagesCompleted
  const classCommentsCount = data?.stats?.classCommentsCount
  const communityPostsCount =
    data?.stats?.communityPostsCount === 0 ? null : data?.stats?.communityPostsCount

  const highlights = getHighlights({
    i18n,
    pagesCompleted,
    classCommentsCount,
    communityPostsCount
  })

  const isValidHighlights = useMemo(() => {
    return Array.isArray(highlights) && highlights.length > 0
  }, [highlights])

  useEffect(() => {
    if (visible && isSuccess && isValidHighlights) {
      sendLoadHighlightsEvent({
        result: TryResult.SUCCESSFUL,
        userUcode,
        classCount: data?.stats?.pagesCompleted || 0,
        commentCount: data?.stats?.classCommentsCount || 0,
        postCount: data?.stats?.communityPostsCount || 0
      })
    }

    if (visible && isError) {
      const failureDescription =
        typeof error === 'object' ? JSON.stringify(error)?.slice(0, 255) : error

      sendLoadHighlightsEvent({
        result: TryResult.FAILURE,
        statusCode: error?.statusCode,
        failureDescription,
        userUcode,
        classCount: 0,
        commentCount: 0,
        postCount: 0
      })
    }
  }, [visible, isSuccess, isError])

  if (isLoading) {
    return (
      <Wrapper className={clsx('_pb-3', className)}>
        <Title className="_text-gray-900 _font-weight-bold _text-uppercase _p-4 _mb-0">
          {i18n.t('profile.highlights.title')}
        </Title>

        <HighlightCardSkeleton length={2} />
      </Wrapper>
    )
  }

  if (isError) {
    return (
      <Wrapper className={clsx('_pb-3', className)}>
        <Title className="_text-gray-900 _font-weight-bold _text-uppercase _p-4 _mb-0">
          {i18n.t('profile.highlights.title')}
        </Title>

        <CoursesErrorState />
      </Wrapper>
    )
  }

  if (!isValidHighlights) {
    return null
  }

  return (
    <Wrapper className={clsx('_pb-3', className)}>
      <Title className="_text-gray-900 _font-weight-bold _text-uppercase _p-4 _mb-0">
        {i18n.t('profile.highlights.title')}
      </Title>

      <SliderWrapper>
        <Slider settings={sliderSettings}>
          {highlights?.map((highlight) => (
            <div key={highlight.label} className="_p-3 _rounded-lg _bg-gray-100">
              <header className="_mb-2">
                <span className="_mr-2">{highlight.icon}</span>
                <CardAmount className="_text-gray-900 _font-weight-bold">
                  {highlight.amount}
                </CardAmount>
              </header>

              <CardLabel className="_d-block _text-gray-900 _text-2">{highlight.label}</CardLabel>
            </div>
          ))}
        </Slider>
      </SliderWrapper>
    </Wrapper>
  )
}

export default Highlights
